import React, { useState, useEffect } from "react";
import addn from "./Images/add_n.gif";
import addo from "./Images/add_o.gif";
import closen from "./Images/close_n.gif";
import closeo from "./Images/close_o.gif";
import editn from "./Images/edit_n.gif";
import edito from "./Images/edit_o.gif";
import modifyn from "./Images/modify_n.gif";
import modifyo from "./Images/modify_o.gif";
import Loading from "../../Common/Loading";
import CustomAlert from "../../Common/CustomAlert";

// --- Configuration ---
const API_BASE =
  process.env.REACT_APP_DOCUMENT_LINK || "https://staging.sysadmin.elearnoncloud.com/";

// --- Interfaces ---
interface UserData {
  SchoolCode: string;
  FamilyCode: string;
  Login: string;
  Password?: string;
  Category: string;
  FName: string;
  LName: string;
  ActivityStatus: string;
  ExpiryDate: string;
  Email: string;
}

const ListSchools: React.FC = () => {
  // --- State ---
  const [data, setData] = useState<UserData[]>([]);
  const [selectedRow, setSelectedRow] = useState<UserData | null>(null);
  const [isSysAdmin, setIsSysAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [alertInfo, setAlertInfo] = useState({ show: false, message: "" });

  const [hoveredBtn, setHoveredBtn] = useState<
    "add" | "modify" | "edit" | "close" | null
  >(null);

  document.body.style.backgroundColor = "#fff";
  // --- Styles ---
  const styles = {
    container: {
      width: "100%",
      fontFamily: "Arial, sans-serif",
      backgroundColor: "#fff", // Ensure white background behind the table
      padding: "10px",
    },
    pageTitle: {
      color: "#2859ac",
      textAlign: "center" as const,
      fontSize: "14px",
      fontWeight: "bold",
    },
    gridContainer: {
      width: "100%",
      height: "auto", // Adjust for title and buttons
      overflow: "auto",
      border: "1px solid #80a0ff",
      borderTop: "none",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse" as const,
      fontSize: "11px", // Matches the small font in screenshot
    },
    th: {
      backgroundColor: "#c7ddfd", // Requested Heading BG
      color: "#2859ac", // Requested Heading Font Color
      fontWeight: "normal",
      padding: "4px 8px",
      border: "1px solid #80a0ff", // Requested Border Color
      position: "sticky" as const,
      top: 0,
      textAlign: "left" as const,
      whiteSpace: "nowrap" as const,
      height: "30px",
      fontSize: "10pt",
    },
    td: {
      padding: "4px 8px",
      borderLeft: "1px solid #a3c0e8", // Requested Border Color
      cursor: "pointer",
      whiteSpace: "nowrap" as const,
      color: "#000",
      height: "30px",
      fontSize: "10pt",
    },
    // Row Styles
    rowEven: {
      backgroundColor: "#ffffff",
    },
    rowOdd: {
      backgroundColor: "#f4faff", // Requested Odd Row BG
    },
    rowSelected: {
      backgroundColor: "#ffa500", // Requested OnClick Row Color
      color: "#fff", // Requested OnClick Text Color
    },
    // Button Styles
    buttonContainer: {
      display: "flex",
      justifyContent: "center",
      gap: "40px", // Spacing between buttons
      marginTop: "15px",
      paddingBottom: "10px",
    },
    actionBtn: {
      backgroundColor: "#ffc107", // Yellow/Orange matches screenshot
      color: "#2859ac", // Blue text usually matches the theme, or black
      border: "none",
      padding: "5px 20px",
      fontSize: "11px",
      fontWeight: "bold",
      cursor: "pointer",
      minWidth: "80px",
      textTransform: "uppercase" as const,
      boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",
    },
  };

  // --- Load Data ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_BASE}api/sysadmin-users-list/`);
        const json = await res.json();

        if (json.error) {
          setError(json.error);
        } else {
          setData(json.data);
          setIsSysAdmin(json.is_sys_admin);
        }
      } catch (err) {
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // --- Handlers ---

  const handleRowClick = (row: UserData) => {
    setSelectedRow(row);
  };

  const showMessage = (msg: string) => {
    setAlertInfo({ show: true, message: msg });
  };

  const validateSelection = () => {
    if (!selectedRow) {
      showMessage("Please Select a row.");
      return false;
    }
    return true;
  };

  const handleAdd = () => {
    window.location.href = `EL_EditLogin?SchoolCode=A&FamilyCode=AA&CalledFrom=L`;
  };

  const handleModify = () => {
    if (!validateSelection()) return;
    const url = `EL_EditLogin?SchoolCode=A&FamilyCode=${selectedRow!.FamilyCode}&Login=${selectedRow!.Login}&CalledFrom=L`;
    window.location.href = url;
  };

  const handleEdit = () => {
    // This corresponds to the legacy 'Roles' button (imgBtnRoles)
    if (!validateSelection()) return;
    const url = `EL_SysAdminRoles?S=A&F=${selectedRow!.FamilyCode}&L=${selectedRow!.Login}`;
    window.location.href = url;
  };

  const handleClose = () => {
    window.close();
  };

  // --- Render ---

  if (loading) return <Loading />;
  if (error) return <div style={{ color: "red" }}>{error}</div>;

  return (
    <div style={styles.container}>
      <div
        style={{
          ...styles.pageTitle,
          backgroundColor: "#c7ddfd",
          padding: "10px",
          border: "1px solid #80a0ff",
          borderBottom: "none",
        }}
      >
        SysAdmin Users List
      </div>

      <div style={styles.gridContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: "20px", textAlign: "center" }}>
                #
              </th>
              <th style={{ ...styles.th, width: "20px", textAlign: "center" }}>
                SchoolCode
              </th>
              <th style={{ ...styles.th, width: "20px", textAlign: "center" }}>
                FamilyCode
              </th>
              <th style={{ ...styles.th, width: "20px", textAlign: "center" }}>
                Login
              </th>
              <th style={{ ...styles.th, width: "20px", textAlign: "center" }}>
                Password
              </th>
              <th style={{ ...styles.th, width: "20px", textAlign: "center" }}>
                Category
              </th>
              <th style={{ ...styles.th, width: "20px", textAlign: "center" }}>
                First name
              </th>
              <th style={{ ...styles.th, width: "20px", textAlign: "center" }}>
                Last Name
              </th>
              <th style={{ ...styles.th, width: "20px", textAlign: "center" }}>
                Status
              </th>
              <th style={{ ...styles.th, width: "20px", textAlign: "center" }}>
                Expiry Date
              </th>
              <th style={{ ...styles.th, width: "1200px", textAlign: "left" }}>
                Email
              </th>
            </tr>{" "}
          </thead>
          <tbody>
            {data.map((row, index) => {
              const isSelected = selectedRow?.Login === row.Login;
              const isOdd = (index + 1) % 2 !== 0;

              // Determine row style based on selection > odd/even
              let rowStyle = isOdd ? styles.rowOdd : styles.rowEven;
              let cellStyle = styles.td;

              if (isSelected) {
                rowStyle = styles.rowSelected;
                cellStyle = { ...styles.td, color: "#fff" };
                // Border color usually stays same or matches background,
                // but prompt asked for specific border color generally.
              }

              return (
                <tr
                  key={row.Login}
                  onClick={() => handleRowClick(row)}
                  style={rowStyle}
                >
                  <td style={{ ...cellStyle, textAlign: "center" }}>
                    {index + 1}
                  </td>
                  <td style={cellStyle}>{row.SchoolCode}</td>
                  <td style={cellStyle}>{row.FamilyCode}</td>
                  <td style={cellStyle}>{row.Login}</td>
                  <td style={cellStyle}>{row.Password}</td>
                  <td style={cellStyle}>{row.Category}</td>
                  <td style={cellStyle}>{row.FName}</td>
                  <td style={cellStyle}>{row.LName}</td>
                  <td style={cellStyle}>{row.ActivityStatus}</td>
                  <td style={cellStyle}>
                    {row.ExpiryDate
                      ? new Date(row.ExpiryDate).toLocaleDateString("en-GB")
                      : ""}
                  </td>
                  <td style={cellStyle}>{row.Email}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ ...styles.buttonContainer }}>
        {/* ADD */}
        <img
          src={hoveredBtn === "add" ? addo : addn}
          alt="Add"
          style={{ cursor: "pointer" }}
          onMouseEnter={() => setHoveredBtn("add")}
          onMouseLeave={() => setHoveredBtn(null)}
          onClick={handleAdd}
        />

        {/* MODIFY */}
        <img
          src={hoveredBtn === "modify" ? modifyo : modifyn}
          alt="Modify"
          style={{ cursor: "pointer" }}
          onMouseEnter={() => setHoveredBtn("modify")}
          onMouseLeave={() => setHoveredBtn(null)}
          onClick={handleModify}
        />

        {/* EDIT (SysAdmin only) */}
        {isSysAdmin && (
          <img
            src={hoveredBtn === "edit" ? edito : editn}
            alt="Edit"
            style={{ cursor: "pointer" }}
            onMouseEnter={() => setHoveredBtn("edit")}
            onMouseLeave={() => setHoveredBtn(null)}
            onClick={handleEdit}
          />
        )}

        {/* CLOSE */}
        <img
          src={hoveredBtn === "close" ? closeo : closen}
          alt="Close"
          style={{ cursor: "pointer" }}
          onMouseEnter={() => setHoveredBtn("close")}
          onMouseLeave={() => setHoveredBtn(null)}
          onClick={handleClose}
        />
      </div>
      {alertInfo.show && (
        <CustomAlert
          message={alertInfo.message}
          onClose={() => setAlertInfo({ ...alertInfo, show: false })}
        />
      )}
    </div>
  );
};

export default ListSchools;
